import { memo } from 'react';

import clsx from 'clsx';

import { Lootbox } from '@/widgets/lootbox/Lootbox';

import { type Prize, WHEEL_PRIZES } from '@/entities/wheel/model/wheel.types';

import { normalizeToValue } from '@/shared/helpers/function';
import { Lottie } from '@/shared/hocs/lottieWrapper';
import { type AnyIcon, Icon } from '@/shared/ui/icon';

import { CONFIG_BY_PRIZE_TYPE } from '../constants';

import s from './index.module.scss';

interface PrizeImageProps {
  prizeType: Prize['type'];
  partSize: number;
  switchIcon?: boolean;
  prize: Prize;

  onComplete?: () => void;

  isLootbox?: boolean;
  onLootboxOpened?: () => void;
}

export const PrizeImage = memo(
  ({
    prizeType,
    partSize,
    switchIcon,
    onComplete,
    prize,
    isLootbox,
    onLootboxOpened,
  }: PrizeImageProps) => {
    if (prizeType === WHEEL_PRIZES.LOOTBOX) return null;

    const config = CONFIG_BY_PRIZE_TYPE[prizeType];

    if (!config) return null;

    const lottie =
      !!config.image && 'lottie' in config.image ? config.image.lottie : undefined;
    const icon = !!config.image && 'icon' in config.image ? config.image.icon : undefined;

    const renderPrizeImage = () => {
      if (icon) {
        return (
          <Icon
            className={clsx(s.svg)}
            icon={normalizeToValue(icon as any, prize) as AnyIcon}
          />
        );
      }

      const lottieClassName = clsx(s.lottie, s[prizeType.toLowerCase()]);

      if (prizeType === WHEEL_PRIZES.TICKET && partSize === 1 && Array.isArray(lottie)) {
        return <Lottie src={lottie[1]} loop autoplay className={lottieClassName} />;
      }

      if (lottie && Array.isArray(lottie)) {
        return (
          <div className={s['switch-lotties']}>
            <Lottie
              src={lottie[0]}
              loop
              autoplay
              isPlaying={!switchIcon}
              resetOnResume
              className={clsx(lottieClassName, switchIcon && s.hidden)}
            />
            <Lottie
              src={lottie[1]}
              loop={false}
              autoplay={false}
              isPlaying={switchIcon}
              className={clsx(
                lottieClassName,
                !switchIcon && s.hidden,
                s['second-lottie']
              )}
              onComplete={onComplete}
            />
          </div>
        );
      }

      return <Lottie src={lottie} loop autoplay className={lottieClassName} />;
    };

    const hasStars = isLootbox || !lottie;

    return (
      <div
        className={clsx(
          s.container,
          s[prizeType.toLowerCase()],
          !lottie && s['with-svg'],
          isLootbox && s['lootbox-wrapper']
        )}
      >
        {hasStars && <div key={isLootbox ? 'lootbox' : 'prize'} className={s.stars} />}
        {isLootbox ? (
          <Lootbox className={s.lootbox} onOpened={onLootboxOpened} />
        ) : (
          renderPrizeImage()
        )}
      </div>
    );
  }
);

PrizeImage.displayName = 'PrizeImage';
